<script lang="ts">
  import { onDestroy, onMount, type Snippet } from "svelte"
  import { clearControlAuthenticatedBrowserState } from "@kontextwerk/control-web-frame/stores/browserState"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { escapeHtml } from "@kontextwerk/web-sdk/core/security"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import ProgressSteps, {
    type ProgressStep,
  } from "@kontextwerk/web-sdk/ui/navigation/ProgressSteps.svelte"
  import { spaNavigate } from "../../../../../../../../../../../../_shared/_logic/navigation"
  import { clearAdminSession } from "../../../../../../../../../../_shared/_logic/admin-session"
  import { resetSignupState } from "../../../../../../../../../../_shared/_logic/signup"
  import {
    completeDemoOrganizationReset,
    getDemoOrganizationResetFormDefaults,
    type DemoDataResetResponse,
  } from "../../../../../../../../../../../../api/kehrblatt/demo-data"
  import {
    LOCKED_WORKFLOW_STEP_MESSAGE,
    selectedWorkflowStepIndex,
    workflowStateForStep,
  } from "../../../../../../../../../../_shared/_logic/workflow-progress"
  import type {
    DemoResetProfileDraft,
    DemoResetProfileOutput,
    DemoResetRunPhase,
    DemoResetStepId,
    DemoResetWorkflowEvent,
    DemoResetWorkflowOutputs,
  } from "./_shared/_logic/demo-data-reset-contract"
  import {
    profileDraftFromDefault,
    profileDraftFromOutput,
  } from "./_shared/_logic/demo-data-reset-contract"
  import { applyDemoResetWorkflowEvent, completedDemoResetSteps } from "./_logic/transitions"
  import AdminStep from "./steps/a_configure_admin"
  import EmployeeStep from "./steps/b_configure_employee"
  import ResetStep from "./steps/c_reset"

  type OverlayHostProps = {
    onremoveOverlay?: (() => void) | null
    onshowOverlay?: (() => void) | null
    reloadBrowser?: () => void
    setCloseDisabled?: ((disabled: boolean) => void) | null
    setActionButtonSnippet?: ((snippet: Snippet | null) => void) | null
  }

  const {
    organizationId,
    onResetDone,
    onremoveOverlay = null,
    onshowOverlay = null,
    reloadBrowser = () => window.location.reload(),
    setCloseDisabled = null,
    setActionButtonSnippet = null,
  }: {
    organizationId: string
    onResetDone: (result: DemoDataResetResponse) => void
  } & OverlayHostProps = $props()

  const stepOrder: DemoResetStepId[] = ["admin", "employee", "reset"]
  let defaultsLoading = $state(true)
  let defaultsError = $state("")
  let outputs = $state<DemoResetWorkflowOutputs>({})
  let selectedStep = $state<DemoResetStepId>("admin")
  let runPhase = $state<DemoResetRunPhase>("ready")
  let adminDraft = $state<DemoResetProfileDraft | null>(null)
  let employeeDraft = $state<DemoResetProfileDraft | null>(null)
  let defaultsController: AbortController | null = null

  const completedSteps = $derived(completedDemoResetSteps(outputs))
  const selectedIndex = $derived(
    selectedWorkflowStepIndex({ stepOrder, completedSteps, selectedStep }),
  )
  const navigationLocked = $derived(
    runPhase === "enqueueing" ||
      runPhase === "running" ||
      runPhase === "status_unavailable" ||
      runPhase === "succeeded_waiting_download" ||
      runPhase === "downloaded",
  )
  const footerDisabled = $derived(
    runPhase === "enqueueing" ||
      runPhase === "running" ||
      runPhase === "succeeded_waiting_download",
  )
  const footerLabel = $derived(runPhase === "ready" ? "Abbrechen" : "Schließen")

  const close = () => onremoveOverlay?.()
  const goToLogin = () => {
    clearControlAuthenticatedBrowserState()
    clearAdminSession()
    resetSignupState()
    spaNavigate("/login", { replace: true, noSearchParam: false })
  }

  const completeSuccess = (result: DemoDataResetResponse) => {
    completeDemoOrganizationReset(organizationId, result)
    onResetDone(result)
    const sessionRevoked = result.current_user_session_revoked
    const loginHint = sessionRevoked
      ? ` Neuer Chef-Login: ${escapeHtml(result.profiles.admin.access_email)} mit dem festgelegten Passwort.`
      : ""
    newNotification({ class: "success", html: `Testdaten wurden zurückgesetzt.${loginHint}` })
    close()

    if (sessionRevoked) window.setTimeout(goToLogin, 300)
    else window.setTimeout(reloadBrowser, 300)
  }

  const requestClose = () => {
    if (footerDisabled) return

    if (runPhase === "downloaded" && outputs.reset) {
      completeSuccess(outputs.reset.result)

      return
    }

    close()
  }

  const applyEvent = (event: DemoResetWorkflowEvent) => {
    outputs = applyDemoResetWorkflowEvent(outputs, event)
  }

  const saveAdmin = (output: DemoResetProfileOutput) => {
    if (adminDraft) adminDraft = profileDraftFromOutput(output, adminDraft)

    applyEvent({ type: "profile_saved", profile: "admin", output })
    selectedStep = "employee"
  }

  const saveEmployee = (output: DemoResetProfileOutput) => {
    if (employeeDraft) employeeDraft = profileDraftFromOutput(output, employeeDraft)

    applyEvent({ type: "profile_saved", profile: "employee", output })
    selectedStep = "reset"
  }

  const editProfile = (profile: "admin" | "employee") => {
    if (navigationLocked) return

    if (profile === "admin" && outputs.admin && adminDraft) {
      adminDraft = profileDraftFromOutput(outputs.admin, adminDraft)
    }

    if (profile === "employee" && outputs.employee && employeeDraft) {
      employeeDraft = profileDraftFromOutput(outputs.employee, employeeDraft)
    }

    applyEvent({ type: "profile_edit_requested", profile })
    selectedStep = profile
  }

  const handleJobStarted = () => {
    applyEvent({ type: "job_started" })

    if (adminDraft) adminDraft = { ...adminDraft, password: "" }

    if (employeeDraft) employeeDraft = { ...employeeDraft, password: "" }

    setCloseDisabled?.(true)
  }

  const handleRunPhase = (phase: DemoResetRunPhase) => {
    runPhase = phase
    setCloseDisabled?.(phase !== "ready" && phase !== "failed" && phase !== "status_unavailable")
  }

  const handleResetOutput = (result: DemoDataResetResponse) => {
    applyEvent({ type: "street_lists_downloaded", result })
  }

  const loadDefaults = async () => {
    defaultsLoading = true
    defaultsError = ""
    const controller = new AbortController()
    defaultsController?.abort()
    defaultsController = controller
    try {
      const loaded = await getDemoOrganizationResetFormDefaults(organizationId, {
        signal: controller.signal,
      })

      if (controller.signal.aborted) return

      adminDraft = profileDraftFromDefault(loaded.profiles.admin)
      employeeDraft = profileDraftFromDefault(loaded.profiles.employee)
    } catch (error) {
      if (controller.signal.aborted) return

      console.error("demo_reset_form_defaults_failed", error)
      defaultsError = "Die bestehenden Zugangsdaten konnten nicht geladen werden."
    } finally {
      if (defaultsController === controller) defaultsController = null

      if (!controller.signal.aborted) defaultsLoading = false
    }
  }

  const handleSelectedIndexChange = (index: number) => {
    const requested = stepOrder[index]

    if (!requested || navigationLocked) return

    if (requested === "employee" && !outputs.admin) return

    if (requested === "reset" && (!outputs.admin || !outputs.employee)) return

    selectedStep = requested
  }

  const rawSteps: ProgressStep[] = $derived([
    {
      id: "admin",
      label: "Chef",
      description: "Zugang und Ansprechpartner-Kontaktdaten des Chefs festlegen.",
      hideDefaultAction: true,
      snippet: adminStep,
    },
    {
      id: "employee",
      label: "Geselle",
      description: "Zugang und Ansprechpartner-Kontaktdaten des Gesellen festlegen.",
      hideDefaultAction: true,
      snippet: employeeStep,
    },
    {
      id: "reset",
      label: "Zurücksetzen",
      description: "Reset ausführen und beide ISI-Exporte herunterladen.",
      hideDefaultAction: true,
      snippet: resetStep,
    },
  ])
  const steps: ProgressStep[] = $derived(
    rawSteps.map((step) => ({
      ...step,
      workflowState: workflowStateForStep({
        step: step.id as DemoResetStepId,
        stepOrder,
        completedSteps,
      }),
    })),
  )

  onMount(() => {
    onshowOverlay?.()
    setActionButtonSnippet?.(footerActions)
    setCloseDisabled?.(false)
    void loadDefaults()
  })

  onDestroy(() => {
    defaultsController?.abort()
    setActionButtonSnippet?.(null)
    setCloseDisabled?.(false)
  })
</script>

<div class="demo-reset-overlay form light" data-testid="kehrblatt.demoReset.workflow">
  <ProgressSteps
    {steps}
    navigationMode="guarded"
    {selectedIndex}
    onSelectedIndexChange={handleSelectedIndexChange}
    lockedStepMessage={LOCKED_WORKFLOW_STEP_MESSAGE}
    showControls={false}
    darkMode={true}
    syncUrl={false}
  />
</div>

{#snippet adminStep(ctx)}
  <AdminStep
    {ctx}
    initial={adminDraft}
    loading={defaultsLoading}
    loadError={defaultsError ||
      (!defaultsLoading && !adminDraft ? "Die benötigten Ausgangsdaten fehlen." : "")}
    onRetry={loadDefaults}
    output={outputs.admin ?? null}
    onOutput={saveAdmin}
    onEdit={() => editProfile("admin")}
  />
{/snippet}

{#snippet employeeStep(ctx)}
  {#if employeeDraft && outputs.admin}
    <EmployeeStep
      {ctx}
      initial={employeeDraft}
      output={outputs.employee ?? null}
      adminAccessEmail={outputs.admin.accessEmail}
      onOutput={saveEmployee}
      onEdit={() => editProfile("employee")}
    />
  {/if}
{/snippet}

{#snippet resetStep(ctx)}
  {#if outputs.admin && outputs.employee}
    <ResetStep
      {ctx}
      {organizationId}
      admin={outputs.admin}
      employee={outputs.employee}
      onJobStarted={handleJobStarted}
      onPhaseChange={handleRunPhase}
      onOutput={handleResetOutput}
    />
  {/if}
{/snippet}

{#snippet footerActions()}
  <CTA
    type="secondaryButton"
    content={footerLabel}
    onClick={requestClose}
    disabled={footerDisabled}
  />
{/snippet}

<style lang="less">
  .demo-reset-overlay {
    color: var(--text-100);
    padding-bottom: 1rem;
  }

  .demo-reset-overlay :global(p) {
    margin: 0;
  }
</style>
